import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import EventTimeline from '../../components/EventTimeline';
import { calculateTimeline } from '../../utils/timelineCalculator';
import './JoinEvent.css';

interface Event {
  id: string;
  name: string;
  description: string;
  city: string;
  scheduledDate: string;
  registrationDeadline: string;
  maxPairs: number;
  currentPairs: number;
  maxDistanceKm?: number;
  organizerName?: string;
  organizerEmail?: string;
  organizerPhone?: string;
  finalGatheringLocation: {
    name: string;
    address: string;
  };
  settings: {
    requirePhoneNumber: boolean;
  };
  schedule?: {
    invitationCloseDaysBeforeEvent: number;
    invitationCloseTime?: string;
    mealAssignmentDaysBeforeEvent: number;
    mealAssignmentTime?: string;
    appetizerDuration: number;
    mainCourseDuration: number;
    dessertDuration: number;
    firstStopEmailMinutesBefore?: number;
  };
}

interface DietaryOption {
  id: string;
  label: string;
  value: string;
}

const JoinEvent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  
  const [step, setStep] = useState<'code' | 'details' | 'partner' | 'hosting' | 'confirmation' | 'waitingList'>('code');
  const [invitationCode, setInvitationCode] = useState(searchParams.get('code') || '');
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [waitingListPosition, setWaitingListPosition] = useState(0);
  
  // Address search state
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  
  // Dietary options from Settings
  const [dietaryOptions, setDietaryOptions] = useState<DietaryOption[]>([]);
  
  // Registration form state
  const [participant1Name, setParticipant1Name] = useState('');
  const [participant1Email, setParticipant1Email] = useState('');
  const [participant1Phone, setParticipant1Phone] = useState('');
  
  const [participant2Name, setParticipant2Name] = useState('');
  const [participant2Email, setParticipant2Email] = useState('');
  const [participant2Phone, setParticipant2Phone] = useState('');
  
  const [hostingAddress, setHostingAddress] = useState('');
  const [hostingMeal, setHostingMeal] = useState<'appetizer' | 'mainCourse' | 'dessert'>('mainCourse');
  const [hostDietaryCapabilities, setHostDietaryCapabilities] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    fetchDietaryOptions();
    fetchUserProfile();
  }, []);

  const fetchDietaryOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/profile/dietary-options`);
      setDietaryOptions(response.data.options);
    } catch (err: any) {
      console.error('Error fetching dietary options:', err);
      // Fallback to defaults if API fails
      setDietaryOptions([
        { id: 'vegetarian', label: 'Vegetar', value: 'vegetarian' },
        { id: 'vegan', label: 'Vegan', value: 'vegan' },
        { id: 'gluten', label: 'Glutenfri', value: 'gluten' },
        { id: 'lactose', label: 'Laktosefri', value: 'lactose' },
        { id: 'nuts', label: 'Nøtteallergi', value: 'nuts' },
        { id: 'shellfish', label: 'Skalldyrallergi', value: 'shellfish' },
        { id: 'fish', label: 'Fiskallergi', value: 'fish' },
        { id: 'eggs', label: 'Eggallergi', value: 'eggs' },
        { id: 'other', label: 'Annet', value: 'other' },
      ]);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/profile`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const profile = response.data;
      setParticipant1Name(profile.name || '');
      setParticipant1Email(profile.email || '');
      setParticipant1Phone(profile.phone || '');
      // Pre-populate hosting address with preferred address from profile
      if (profile.preferredAddress) {
        setHostingAddress(profile.preferredAddress);
      }
    } catch (err: any) {
      console.error('Error fetching user profile:', err);
      // Fallback to user from context
      if (user) {
        setParticipant1Name(user.name || '');
        setParticipant1Email(user.email || '');
      }
    }
  };

  const lookupPartnerByEmail = async (email: string) => {
    // Only lookup if email looks valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return;
    }

    try {
      // Try to fetch user by email (we'll need a backend endpoint for this)
      const response = await axios.get(`${API_URL}/users/lookup?email=${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      if (response.data.user) {
        const partner = response.data.user;
        setParticipant2Name(partner.name || '');
        setParticipant2Phone(partner.phone || '');
      }
    } catch (err: any) {
      // If user not found, just continue - they can enter manually
      console.log('Partner not found in database, manual entry required');
    }
  };

  // Auto-validate code from URL on mount
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl && codeFromUrl.trim()) {
      setInvitationCode(codeFromUrl.toUpperCase());
      // Auto-validate after a brief delay to ensure state is set
      setTimeout(() => {
        validateCodeFromUrl(codeFromUrl.toUpperCase());
      }, 100);
    }
  }, []);

  const validateCodeFromUrl = async (code: string) => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API_URL}/events/validate-code/${code}`);
      setEvent(response.data.event);
      setStep('details');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ugyldig invitasjonskode');
      setStep('code'); // Stay on code entry if invalid
    } finally {
      setLoading(false);
    }
  };

  const handleValidateCode = async () => {
    if (!invitationCode) {
      setError('Vennligst oppgi invitasjonskode');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API_URL}/events/validate-code/${invitationCode.toUpperCase()}`);
      setEvent(response.data.event);
      setStep('details');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ugyldig invitasjonskode');
    } finally {
      setLoading(false);
    }
  };

  const searchAddress = async (query: string) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    setIsSearchingAddress(true);
    try {
      const response = await fetch(
        `https://ws.geonorge.no/adresser/v1/sok?sok=${encodeURIComponent(query)}&treffPerSide=10`
      );
      const data = await response.json();
      
      if (data.adresser && data.adresser.length > 0) {
        const suggestions = data.adresser.map((addr: any) => ({
          address: addr.adressetekst,
          municipality: addr.poststed || addr.kommunenavn,
          position: null
        }));
        setAddressSuggestions(suggestions);
        setShowAddressSuggestions(true);
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    } catch (err) {
      console.error('Address search error:', err);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHostingAddress(value);
    searchAddress(value);
  };

  const selectAddress = (suggestion: any) => {
    const fullAddress = suggestion.municipality 
      ? `${suggestion.address}, ${suggestion.municipality}`
      : suggestion.address;
    setHostingAddress(fullAddress);
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
  };

  const handleHostDietaryChange = (value: string) => {
    setHostDietaryCapabilities(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleSubmitRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!participant1Name || !participant1Email) {
      setError('Vennligst fyll inn dine opplysninger');
      return;
    }

    if (!participant2Name || !participant2Email) {
      setError('Vennligst fyll inn opplysninger for begge deltakere');
      return;
    }

    if (!participant1Phone || !participant2Phone) {
      setError('Telefonnummer er påkrevd for alle deltakere');
      return;
    }

    if (!hostingAddress) {
      setError('Vennligst oppgi adressen der dere skal være verter');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(participant1Email) || !emailRegex.test(participant2Email)) {
      setError('Vennligst oppgi gyldige e-postadresser');
      return;
    }

    setLoading(true);

    try {
      const registrationData = {
        invitationCode: invitationCode.toUpperCase(),
        participant1: {
          name: participant1Name,
          email: participant1Email,
          phone: participant1Phone || undefined,
        },
        participant2: {
          name: participant2Name,
          email: participant2Email,
          phone: participant2Phone || undefined,
        },
        address: hostingAddress,
        hostingMeal,
        hostDietaryCapabilities: hostDietaryCapabilities.length > 0 ? hostDietaryCapabilities : undefined,
        notes: notes || undefined,
      };

      await axios.post(
        `${API_URL}/events/register`,
        registrationData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          timeout: 30000, // 30 second timeout
        }
      );

      setStep('confirmation');
    } catch (err: any) {
      console.error('Registration error:', err);
      if (err.code === 'ECONNABORTED') {
        setError('Registreringen tok for lang tid. Vennligst prøv igjen.');
      } else if (err.response?.data?.status === 'waitingList') {
        // User added to waiting list
        setStep('waitingList');
        setWaitingListPosition(err.response.data.position);
      } else {
        setError(err.response?.data?.message || 'Kunne ikke registrere. Prøv igjen.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderCodeStep = () => (
    <div className="join-event-card">
      <h1>Bli med på Sykkelfest!</h1>
      <p className="subtitle">
        Skriv inn invitasjonskoden du har mottatt fra arrangøren for å registrere deg.
      </p>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading-state">
          <div className="spinner-large">
            <svg className="spinner-icon" viewBox="0 0 50 50">
              <circle
                className="spinner-path"
                cx="25"
                cy="25"
                r="20"
                fill="none"
                strokeWidth="4"
              ></circle>
            </svg>
          </div>
          <p>Validerer invitasjonskode...</p>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); handleValidateCode(); }}>
          <div className="form-group">
            <label htmlFor="invitationCode" className="form-label">
              Invitasjonskode
            </label>
            <input
              type="text"
              id="invitationCode"
              className="form-input code-input"
              value={invitationCode}
              onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
              placeholder="ABC12345"
              maxLength={8}
              disabled={loading}
              autoFocus
            />
            <p className="form-hint">Koden er vanligvis 8 tegn lang</p>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading || !invitationCode}
          >
            {loading ? 'Validerer...' : 'Fortsett'}
          </button>
        </form>
      )}
    </div>
  );

  const renderDetailsStep = () => {
    if (!event) return null;

    const spotsLeft = event.maxPairs - event.currentPairs;
    const deadline = new Date(event.registrationDeadline);
    const eventDate = new Date(event.scheduledDate);

    // Calculate timeline if schedule data is available
    let timeline = null;
    if (event.schedule) {
      timeline = calculateTimeline(event.scheduledDate, event.schedule);
    }

    return (
      <div className="join-event-card">
        <div className="event-header">
          <h1>Sykkelfesten {event.name}</h1>
          <span className={`badge ${spotsLeft > 5 ? 'badge-success' : 'badge-warning'}`}>
            {spotsLeft} par ledige
          </span>
          <p>
          Du er invitert til en unik sykkelfestopplevelse! Les gjennom detaljene nedenfor før du fortsetter med påmeldingen.
          </p>
        </div>

        <div className="info-box">
          <h3>📋 Hva er en Sykkelfest?</h3>
          <p>
            En Sykkelfest er en sosial middag der du sykler mellom tre forskjellige hjem sammen med en partner.
            Dere vil være verter for én rett, og gjester ved to andre retter. Det er en perfekt måte å møte nye mennesker på!
          </p>
          <ul>
            <li>✅ Dere melder dere på som et par (2 personer)</li>
            <li>✅ Dere blir tildelt én rett å være verter for (forrett, hovedrett eller dessert)</li>
            <li>✅ Dere får en rute med tre stopp</li>
            <li>✅ Møt 6 andre par gjennom kvelden (12 personer)!</li>
            <li>✅ Langs ruten kan dere skimte andre sykkelfestere på vei til sine stopp.</li>
            <li>✅ Alle møtes til slutt på en felles lokasjon for en avsluttende fest!</li>
          </ul>
        </div>

        <div className="banner-image-section">
          <img 
            src="/sykkelfest-top-banner.png" 
            alt="Sykkelfest banner" 
            className="banner-image"
          />
        </div>

        <h2 className="section-title">Du er invitert til {event.name}</h2>

        <div className="event-details">
          <div className="detail-item">
            <span className="detail-icon">📍</span>
            <div>
              <strong>Sted</strong>
              <p>{event.city}</p>
            </div>
          </div>

          <div className="detail-item">
            <span className="detail-icon">📅</span>
            <div>
              <strong>Dato</strong>
              <p>{eventDate.toLocaleDateString('nb-NO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p className="time">{eventDate.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          </div>

          <div className="detail-item">
            <span className="detail-icon">⏰</span>
            <div>
              <strong>Påmeldingsfrist</strong>
              <p>{deadline.toLocaleDateString('nb-NO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p className="time">{deadline.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          </div>

          <div className="detail-item">
            <span className="detail-icon">🎯</span>
            <div>
              <strong>Siste samling</strong>
              <p>{event.finalGatheringLocation.name}</p>
              <p className="address">{event.finalGatheringLocation.address}</p>
            </div>
          </div>

          {event.maxDistanceKm && (
            <div className="detail-item">
              <span className="detail-icon">📍</span>
              <div>
                <strong>Geografisk begrensning</strong>
                <p className="address">
                  Du må bo innenfor {event.maxDistanceKm} km fra sluttstedet for å kunne delta. 
                  Dette sikrer overkommelige sykkelavstander for alle.
                </p>
              </div>
            </div>
          )}

          {event.description && (
            <div className="event-description">
              <strong>Om arrangementet</strong>
              <p>{event.description}</p>
            </div>
          )}

          {event.organizerName && event.organizerEmail && (
            <div className="detail-item" style={{ backgroundColor: '#F0F9FF', borderLeft: '4px solid #2563EB', padding: '16px', borderRadius: '8px', marginTop: '16px' }}>
              <span className="detail-icon">📞</span>
              <div>
                <strong style={{ color: '#2563EB' }}>Kontakt arrangør</strong>
                <p><strong>Navn:</strong> {event.organizerName}</p>
                <p><strong>E-post:</strong> <a href={`mailto:${event.organizerEmail}`} style={{ color: '#2563EB' }}>{event.organizerEmail}</a></p>
                {event.organizerPhone && (
                  <p><strong>Telefon:</strong> <a href={`tel:${event.organizerPhone}`} style={{ color: '#2563EB' }}>{event.organizerPhone}</a></p>
                )}
              </div>
            </div>
          )}
        </div>

        {timeline && (
          <div className="event-details">
            <h3 style={{ marginTop: 'var(--spacing-xl)', marginBottom: 'var(--spacing-md)', color: 'var(--primary-color)', fontSize: '1.25rem' }}>
              Tidsplan for {event.name}
            </h3>
            
            <div className="detail-item">
              <span className="detail-icon">🍽️</span>
              <div>
                <strong>{timeline.mealAssignmentDate}, kl. {timeline.mealAssignmentTime}</strong>
                <p className="address">Matretter tildeles de forskjellige par og dere får beskjed om hva dere skal servere. Da har dere god tid til å kose dere med forberedelser.</p>
              </div>
            </div>

            <div className="detail-item">
              <span className="detail-icon">📱</span>
              <div>
                <strong>{timeline.eventDate}, kl. {timeline.firstMessageTime}</strong>
                <p className="address">Melding om første treffpunkt sendes til deltakere. Det blir spennende å se hvem dere møter!</p>
              </div>
            </div>

            <div className="detail-item">
              <span className="detail-icon">🥗</span>
              <div>
                <strong>{timeline.eventDate}, kl. {timeline.starterTime}</strong>
                <p className="address">Forrett serveres på ditt første treffpunkt. Nyt forretten sammen med to andre par.</p>
              </div>
            </div>

            <div className="detail-item">
              <span className="detail-icon">📱</span>
              <div>
                <strong>{timeline.eventDate}, kl. {timeline.mainCourseMessageTime}</strong>
                <p className="address">Melding om neste treffpunkt sendes ut. På tide å bryte opp og sykle videre til hovedretten - men husk, dere møtes igjen på sluttfesten.</p>
              </div>
            </div>

            <div className="detail-item">
              <span className="detail-icon">🍝</span>
              <div>
                <strong>{timeline.eventDate}, kl. {timeline.mainCourseTime}</strong>
                <p className="address">Hovedrett serveres på ditt andre treffpunkt, med nye mennesker i godt lag. Nyt maten og selskapet!</p>
              </div>
            </div>

            <div className="detail-item">
              <span className="detail-icon">📱</span>
              <div>
                <strong>{timeline.eventDate}, kl. {timeline.dessertMessageTime}</strong>
                <p className="address">Melding om neste treffpunkt sendes ut. Nå nærmer det seg dessert og sluttfest!</p>
              </div>
            </div>

            <div className="detail-item">
              <span className="detail-icon">🍰</span>
              <div>
                <strong>{timeline.eventDate}, kl. {timeline.dessertTime}</strong>
                <p className="address">Dessert serveres på ditt tredje treffpunkt, nyt søtsaken og stemningen - her er 2 nye par å bli kjent med!</p>
              </div>
            </div>

            <div className="detail-item">
              <span className="detail-icon">🎉</span>
              <div>
                <strong>{timeline.eventDate}, kl. {timeline.finalPartyTime}</strong>
                <p className="address">Alle samles for en hyggelig avslutning på {event.finalGatheringLocation.name}. Her møter du igjen de du har delt kvelden med, samt alle andre som har sine egne unike ruter på Sykkelfesten.</p>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={() => setStep('partner')}
          className="btn btn-primary btn-full"
        >
          Start påmelding
        </button>
        <button
          onClick={() => setStep('code')}
          className="btn btn-secondary btn-full"
        >
          Tilbake
        </button>
      </div>
    );
  };

  const renderPartnerStep = () => (
    <div className="join-event-card">
      <h2>Deltakerdetaljer</h2>
      <p className="subtitle">Fyll inn opplysninger for begge deltakere i paret</p>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={(e) => { e.preventDefault(); setStep('hosting'); }}>
        <div className="participant-section">
          <h3>👤 Deltaker 1 (Deg)</h3>
          
          <div className="form-group">
            <label className="form-label">Fullt navn *</label>
            <input
              type="text"
              className="form-input"
              value={participant1Name}
              onChange={(e) => setParticipant1Name(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">E-post *</label>
            <input
              type="email"
              className="form-input"
              value={participant1Email}
              onChange={(e) => setParticipant1Email(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Telefonnummer *
            </label>
            <input
              type="tel"
              className="form-input"
              value={participant1Phone}
              onChange={(e) => setParticipant1Phone(e.target.value)}
              placeholder="+47 123 45 678"
              required
            />
            <p className="form-hint">
              Kostholdinformasjon hentes fra din profil. Oppdater profilen din hvis nødvendig.
            </p>
          </div>
        </div>

        <div className="participant-section">
          <h3>👤 Deltaker 2 (Din partner)</h3>
          
          <div className="form-group">
            <label className="form-label">Fullt navn *</label>
            <input
              type="text"
              className="form-input"
              value={participant2Name}
              onChange={(e) => setParticipant2Name(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">E-post *</label>
            <input
              type="email"
              className="form-input"
              value={participant2Email}
              onChange={(e) => setParticipant2Email(e.target.value)}
              onBlur={(e) => lookupPartnerByEmail(e.target.value)}
              required
            />
            <p className="form-hint">
              Hvis din partner allerede har en konto, hentes navn og telefon automatisk.
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">
              Telefonnummer *
            </label>
            <input
              type="tel"
              className="form-input"
              value={participant2Phone}
              onChange={(e) => setParticipant2Phone(e.target.value)}
              placeholder="+47 123 45 678"
              required
            />
          </div>
        </div>

        <button type="submit" className="btn btn-primary btn-full">
          Fortsett
        </button>
        <button
          type="button"
          onClick={() => setStep('details')}
          className="btn btn-secondary btn-full"
        >
          Tilbake
        </button>
      </form>
    </div>
  );

  const renderHostingStep = () => (
    <div className="join-event-card">
      <h2>Vertsdetaljer</h2>
      <p className="subtitle">
        Som et par vil dere være verter for én av de tre rettene. Oppgi adressen der dere ønsker å være verter.
      </p>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmitRegistration}>
        <div className="form-group">
          <label className="form-label">Vertsadresse *</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              className="form-input"
              value={hostingAddress}
              onChange={handleAddressChange}
              onFocus={() => hostingAddress.length >= 3 && addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
              placeholder="Gateadresse, postnummer, sted"
              required
            />
            {isSearchingAddress && (
              <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>Søker...</span>
              </div>
            )}
            {showAddressSuggestions && addressSuggestions.length > 0 && (
              <div className="address-suggestions">
                {addressSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="address-suggestion-item"
                    onClick={() => selectAddress(suggestion)}
                  >
                    <div className="suggestion-main">{suggestion.address}</div>
                    {suggestion.municipality && (
                      <div className="suggestion-municipality">{suggestion.municipality}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="form-hint">
            Dette er adressen der dere vil være verter. Den brukes også for ruteplanlegging.
          </p>
        </div>

        <div className="form-group">
          <label className="form-label">Ønsket rett å være verter for *</label>
          <div className="meal-options">
            <label className="meal-option">
              <input
                type="radio"
                name="hostingMeal"
                value="appetizer"
                checked={hostingMeal === 'appetizer'}
                onChange={(e) => setHostingMeal(e.target.value as any)}
              />
              <span className="meal-icon">🥗</span>
              <span>Forrett</span>
            </label>
            <label className="meal-option">
              <input
                type="radio"
                name="hostingMeal"
                value="mainCourse"
                checked={hostingMeal === 'mainCourse'}
                onChange={(e) => setHostingMeal(e.target.value as any)}
              />
              <span className="meal-icon">🍝</span>
              <span>Hovedrett</span>
            </label>
            <label className="meal-option">
              <input
                type="radio"
                name="hostingMeal"
                value="dessert"
                checked={hostingMeal === 'dessert'}
                onChange={(e) => setHostingMeal(e.target.value as any)}
              />
              <span className="meal-icon">🍰</span>
              <span>Dessert</span>
            </label>
          </div>
          <p className="form-hint">
            Merk: Den endelige rettfordelingen bestemmes av rutealgoritmen for optimal sykkelrute.
          </p>
        </div>

        <div className="form-group">
          <label className="form-label">Hvilke kosthold kan dere tilby?</label>
          <p className="form-hint" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
            Velg alle kosthold/allergier dere kan tilrettelegge for når dere er verter.
          </p>
          <div className="dietary-options">
            {dietaryOptions.map((option) => (
              <label key={option.id} className="dietary-checkbox">
                <input
                  type="checkbox"
                  checked={hostDietaryCapabilities.includes(option.value)}
                  onChange={() => handleHostDietaryChange(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Melding til dine gjester (valgfritt)</label>
          <textarea
            className="form-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="F.eks. parkeringsinformasjon, inngangsinstruksjoner, eller en hilsen til gjestene..."
          />
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-full"
          disabled={loading}
        >
          {loading && (
            <span className="spinner" role="status" aria-label="Laster">
              <svg className="spinner-icon" viewBox="0 0 50 50">
                <circle
                  className="spinner-path"
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  strokeWidth="4"
                ></circle>
              </svg>
            </span>
          )}
          {loading ? 'Registrerer...' : 'Fullfør påmelding'}
        </button>
        <button
          type="button"
          onClick={() => setStep('partner')}
          className="btn btn-secondary btn-full"
          disabled={loading}
        >
          Tilbake
        </button>
      </form>
    </div>
  );

  const renderConfirmationStep = () => {
    if (!event) return null;

    // Calculate timeline if schedule data is available
    let timeline = null;
    if (event.schedule) {
      timeline = calculateTimeline(event.scheduledDate, event.schedule);
    }

    return (
      <div className="join-event-card confirmation">
        <div className="success-icon">✅</div>
        <h1>Påmelding fullført!</h1>
        <p className="subtitle">
          Dere er nå påmeldt <strong>{event?.name}</strong>!
        </p>

        <div className="confirmation-details">
          <h3>Hva skjer nå?</h3>
          <ol>
            <li>
              <strong>Bekreftelse på e-post</strong><br />
              Både {participant1Email} og {participant2Email} vil motta en bekreftelse på e-post.
            </li>
            <li>
              <strong>Ruteplanlegging</strong><br />
              Etter påmeldingsfristen vil arrangøren generere ruter for alle par.
            </li>
            <li>
              <strong>Få din rute</strong><br />
              Dere vil motta detaljert informasjon om deres rute og hvem dere skal møte.
            </li>
            <li>
              <strong>Forbered maten</strong><br />
              Planlegg menyen for den retten dere skal være verter for!
            </li>
          </ol>
        </div>

        {timeline && (
          <div className="event-timeline-section">
            <h3>Tidsplan for arrangementet</h3>
            <EventTimeline eventName={event.name} eventDate={event.scheduledDate} timeline={timeline} />
          </div>
        )}

        <div className="actions">
          <button
            className="btn btn-primary"
            onClick={() => navigate('/deltaker/mine-fester')}
          >
            Se dine arrangementer
          </button>
        </div>
      </div>
    );
  };

  const renderWaitingListStep = () => {
    if (!event) return null;

    return (
      <div className="join-event-card confirmation waiting-list">
        <div className="waiting-icon">⏰</div>
        <h1>På venteliste</h1>
        <p className="subtitle">
          Arrangementet <strong>{event.name}</strong> er for øyeblikket fullt.
        </p>

        <div className="waiting-list-info">
          <div className="position-badge">
            <span className="position-number">{waitingListPosition}</span>
            <span className="position-label">i køen</span>
          </div>
          
          <div className="confirmation-details">
            <h3>Hva betyr dette?</h3>
            <ul>
              <li>
                <strong>Du er nå på ventelisten</strong><br />
                Hvis noen melder seg av, vil du automatisk få beskjed på e-post.
              </li>
              <li>
                <strong>Sjekk e-posten din</strong><br />
                Vi sender deg en bekreftelse på at du står på ventelisten.
              </li>
              <li>
                <strong>Vær rask hvis det blir plass</strong><br />
                Når du får beskjed om ledig plass, må du melde deg på raskt før andre tar plassen.
              </li>
            </ul>
          </div>
        </div>

        <div className="actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/')}
          >
            Tilbake til forsiden
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="join-event-page">
      <div className="join-event-container">
        {step === 'code' && renderCodeStep()}
        {step === 'details' && renderDetailsStep()}
        {step === 'partner' && renderPartnerStep()}
        {step === 'hosting' && renderHostingStep()}
        {step === 'confirmation' && renderConfirmationStep()}
        {step === 'waitingList' && renderWaitingListStep()}
      </div>
    </div>
  );
};

export default JoinEvent;
